import {Tooltip} from '@mui/material';
import {Button, type ButtonProps, InfoTooltip} from '@wandb/components';
import {Button as OldButton} from '@wandb/weave/components/Button';
import React, {type JSX, useContext} from 'react';

import {entryPointPlaceholders, WBAgentEntryPointButton} from '../../../aria';
import {LoginPopup} from '../../../components/LoginPopup';
import {
  ReportDiscussionContext,
  ReportDiscussionUpdaterContext,
} from '../../../components/ReportDiscussion/ReportDiscussionContext';
import {ShareReportTrigger} from '../../../components/ShareReportTrigger/ShareReportTrigger';
import {useNwEntityPermissionResult} from '../../../state/graphql/namedWorkspaces';
import {accessIsRestricted} from '../../../util/permissions';
import {useReportStar} from '../hooks';
import {useLoadedReportContext} from '../LoadedReportContext';
import {useReportPageContext} from '../ReportPageContext';
import * as S from '../ReportPageHeader/ReportPageHeader.styles';
import {ReportPageMenuActions} from '../ReportPageHeader/ReportPageMenuActions';

type ReportPageViewHeaderProps = {
  viewerOwnsReport: boolean;
  isFloatingBar?: boolean;
  shareReportVariant?: ButtonProps['variant'];
  loginPopupOffset?: number;
};

interface ConditionalWrapperProps {
  condition: boolean;
  children?: React.ReactNode;
}

const ConditionalWrapper = ({
  condition,
  children,
}: ConditionalWrapperProps): JSX.Element => {
  return condition ? (
    <S.ReportPageHeader className="hide-on-print">
      {children}
    </S.ReportPageHeader>
  ) : (
    <>{children}</>
  );
};

export const ReportPageViewHeader = ({
  viewerOwnsReport,
  isFloatingBar,
  shareReportVariant,
  loginPopupOffset,
}: ReportPageViewHeaderProps) => {
  const {
    isAdmin,
    viewer,
    rootReportId,
    entityName,
    isPublicProject,
    project,
    teamMemberInfo: {viewerIsTeamAdmin, viewerIsTeamMember},
  } = useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();
  const isLoggedIn = viewer !== undefined && viewer !== null;

  const {
    starCount = 0,
    starred,
    toggleStar,
  } = useReportStar({
    reportId: rootReportId,
    viewer,
    reportViewRef,
    isPublicProject,
  });

  const {setCommentFrame} = useContext(ReportDiscussionUpdaterContext);
  const {commentsCount} = useContext(ReportDiscussionContext);

  const {data: entityPermissionData} = useNwEntityPermissionResult({
    entityName: entityName,
  });

  const isTeamHidden = entityPermissionData?.isTeamHidden ?? false;
  const isProjectRestricted = accessIsRestricted(project.access);
  const shareDisabledReason = isTeamHidden
    ? 'Report sharing is disabled for hidden teams.'
    : isProjectRestricted
      ? 'Report sharing is disabled because this project is restricted.'
      : undefined;

  const hasShareAccess =
    viewerOwnsReport ||
    viewerIsTeamMember ||
    !view.locked ||
    isAdmin ||
    isPublicProject;
  return (
    <ConditionalWrapper condition={!isFloatingBar}>
      {!isFloatingBar && viewerOwnsReport && (
        <WBAgentEntryPointButton
          data-dd-action-name="Edit report with agent"
          entryPoint="report_header"
          label="Edit with ARIA"
          placeholder={entryPointPlaceholders.improveReport}
        />
      )}
      {hasShareAccess && (
        <>
          {shareDisabledReason != null && (
            <InfoTooltip
              id="share-report-disabled-tooltip"
              aria-label={shareDisabledReason}>
              {shareDisabledReason}
            </InfoTooltip>
          )}
          <ShareReportTrigger
            viewRef={reportViewRef}
            view={view}
            userIsMemberOfTeam={viewerIsTeamMember}
            userIsTeamAdmin={viewerIsTeamAdmin}
            shouldShowInviteTab={!!viewer}>
            <Button
              data-test="share-report-button"
              startIcon="export-share-upload"
              isDisabled={shareDisabledReason != null}
              variant={shareReportVariant ? shareReportVariant : 'primary'}>
              Share
            </Button>
          </ShareReportTrigger>
        </>
      )}
      <LoginPopup
        isLoggedIn={isLoggedIn}
        sideOffset={loginPopupOffset ?? 8}
        align="end"
        alignOffset={-120}>
        <Button
          data-test="comment-report-button"
          startIcon="forum-chat-bubble"
          variant="ghost"
          tooltip={{children: 'Add a comment'}}
          onPress={() => {
            if (isLoggedIn) {
              setCommentFrame('editor');
            }
          }}>
          {commentsCount === 0
            ? 'Comment'
            : `${commentsCount} comment${commentsCount > 1 ? 's' : ''}`}
        </Button>
      </LoginPopup>
      <LoginPopup
        isLoggedIn={isLoggedIn}
        sideOffset={loginPopupOffset ?? 8}
        align="end"
        alignOffset={-40}>
        <Tooltip title="Star this report">
          <span>
            <OldButton
              data-test="star-report-button"
              icon={starred ? 'star-filled' : 'star'}
              variant="ghost"
              onClick={() => {
                if (isLoggedIn) {
                  toggleStar();
                }
              }}>
              {!starCount // if starCount is 0, null, or undefined
                ? 'Star'
                : `${starCount} star${starCount > 1 ? 's' : ''}`}
            </OldButton>
          </span>
        </Tooltip>
      </LoginPopup>
      {!isFloatingBar && (
        <ReportPageMenuActions viewerOwnsReport={viewerOwnsReport} />
      )}
    </ConditionalWrapper>
  );
};
